import { makeAutoObservable } from "mobx";
import apiClient, { plainApiClient } from "../api";
import {
  clientGenerateOtp,
  clientVerifyOtp,
  employeeLogin,
  getAuthPublicKey,
  liasionGenerateOtp,
  liasionVerifyOtp,
} from "../api/hooks/Auth";
import { getBrowserDetails, getOperatingSystem } from "../utils/functions";
import { getImageSrc } from "../utils/data";
import { setCookie } from "../utils/cookie";
import { Buffer } from "buffer";
import UserStore from "./UserStore";

class AuthStore {
  isServerDown = "default";
  isLoadingFullScreen = false;
  isLoadingForLogin = false;
  isfirstTimeFamilySelected = false;
  isLoadingForFeedback = false;
  isLoadingForUploadScreen = false;
  isLoadingForSettingsLO = false;
  selectFamiliesPageAllImages = {};
  loginRandomImage = getImageSrc(Math.floor(Math.random() * 5));
  blockedUserData = null;
  healthCheckData = "0";
  isSessionExpired = false;
  isSessionExpiredFromAPI = false;
  resolveFn = null;
  aesKey = null;
  publicKeyData = {
    isFetching: null,
    fetchStatus: "default",
    publicKey: null,
  };
  publicKeyPromise = null;

  constructor() {
    makeAutoObservable(this);
  }

  updateSelectFamiliesPageAllImages(data) {
    this.selectFamiliesPageAllImages = {
      ...this.selectFamiliesPageAllImages,
      ...data,
    };
  }

  updateIsfirstTimeFamilySelected(state) {
    this.isfirstTimeFamilySelected = state;
  }
  updateLoadingForLogin(isLoading) {
    this.isLoadingForLogin = isLoading;
  }
  updateLoadingForFeedback(isLoading) {
    this.isLoadingForFeedback = isLoading;
  }
  updateLoadingUploadScreen(isLoading) {
    this.isLoadingForUploadScreen = isLoading;
  }
  updateLoadingFullScreen(isLoading, data) {
    this.isLoadingFullScreen = isLoading;
  }
  updateLoadingForSettingsLO(isLoading) {
    this.isLoadingForSettingsLO = isLoading;
  }
  _liasionOfficerGenerateOtp({ MobileNo, EmailId }, { onSuccess, onFail }) {
    liasionGenerateOtp({
      MobileNo,
      EmailId,
    })
      .then(({ data }) => {
        onSuccess(data);
      })
      .catch(({ response: { data } }) => {
        onFail(data);
      });
  }

  _liasionOfficerVerifyOtp(
    { MobileNo, Otp, SessionId, EmailId, IsPoint },
    { onSuccess, onFail }
  ) {
    liasionVerifyOtp({
      MobileNo,
      Otp,
      SessionId,
      EmailId,
      IsPoint,
    })
      .then(({ data }) => {
        onSuccess(data);
      })
      .catch((error) => {
        if (error?.response?.data) {
          onFail(error?.response?.data);
          return;
        }
        onFail({
          statusMessage: "Oops! Something went wrong. Please try again later",
        });
      });
  }

  _clientVerifyOtp({ otp, sessionId, PanNo, IsPoint }, { onSuccess, onFail }) {
    this.updateLoadingForLogin(true);
    clientVerifyOtp({
      otp: otp,
      sessionId,
      PanNo,
      IsPoint,
    })
      .then(({ data }) => {
        onSuccess(data);
        this.updateLoadingForLogin(false);
      })
      .catch((error) => {
        this.updateLoadingForLogin(false);
        if (error?.response?.data) {
          onFail(error?.response?.data);
          return;
        }
        onFail({
          statusMessage: "Oops! Something went wrong. Please try again later",
        });
      });
  }

  _clientGenerateOtp(PanNo, { onSuccess, onFail }) {
    this.updateLoadingForLogin(true);
    clientGenerateOtp(PanNo)
      .then(({ data }) => {
        onSuccess(data);
        this.updateLoadingForLogin(false);
      })
      .catch((error) => {
        this.updateLoadingForLogin(false);
        if (error?.response?.data) {
          onFail(error?.response?.data);
          return;
        }
        onFail({
          statusMessage: "Oops! Something went wrong. Please try again later",
        });
      });
  }

  addRegisteredDevice() {
    const payload = {
      DeviceId: UserStore.userDeviceId,
      DeviceType: getBrowserDetails(),
      DeviceOS: getOperatingSystem(window),
      DeviceBuild: "Nuvama Web",
    };

    apiClient
      .post(`/registereddevices`, payload)
      .then(({ data }) => {})
      .catch((error) => {});
  }

  updateLoginJourney() {
    apiClient
      .get(`/UpdateLoginJourney`)
      .then(({ data }) => {})
      .catch((error) => {});
  }

  updatehealthCheckData(data) {
    this.healthCheckData = data;
  }

  getHealthCheckData(userId, onSuccess, onFail, loginType) {
    apiClient
      .get(`/HealthCheckData?userid=${userId}`)
      .then(({ data }) => {
        onSuccess(data);
        if (!loginType) this.updatehealthCheckData(data?.[0]?.data);
      })
      .catch((error) => {
        onFail(error);
      });
  }

  setAuthSession({ AuthToken, SecreteKey, key }) {
    setCookie("token", AuthToken);
    setCookie("secreteKey", Buffer.from(SecreteKey, "base64").toString());
  }

  getServerMsg({ onSuccess, onFail }) {
    plainApiClient
      .get("/GETServerDetails")
      .then((response) => {
        if (
          response.status === 200 &&
          response.data.message !== "Server down for maintenance"
        ) {
          this.isServerDown = false;
          if (!onSuccess) {
            this.updateLoadingForLogin(false);
          } else {
            onSuccess({ data: response.data });
          }
        } else if (
          response.status === 200 &&
          response.data.message === "Server down for maintenance"
        ) {
          this.isServerDown = true;
          this.updateLoadingForLogin(false);
          if (onFail) {
            onFail();
          }
        } else if (response.status === 509) {
          this.isServerDown = true;
          this.updateLoadingForLogin(false);
          if (onFail) {
            onFail();
          }
        } else if (response.status === 503) {
          this.isServerDown = true;
          this.updateLoadingForLogin(false);
          if (onFail) {
            onFail();
          }
        }
        this.updateLoadingFullScreen(false);
      })
      .catch((error) => {
        this.isServerDown = true;
        this.updateLoadingForLogin(false);
        this.updateLoadingFullScreen(false);
        if (onFail) {
          onFail();
        }
      });
  }

  getPublicKey({ onSuccess = () => {}, onFail = () => {} }) {
    getAuthPublicKey()
      .then((res) => {
        onSuccess({ data: res.data });
      })
      .catch((error) => {
        onFail({ error });
      });
  }

  redirectToOldVersionLogin() {
    apiClient
      .get("/auth/LoginLogDetails")
      .then(() => {})
      .catch((error) => {});
  }

  redirectToOldVersionHeader() {
    apiClient
      .get("/client/LoginLogDetails")
      .then(() => {})
      .catch((error) => {});
  }

  setData(keyName, value) {
    this[keyName] = value;
  }

  async ensurePublicKey() {
    if (this.publicKeyData.publicKey) return;

    if (!this.publicKeyPromise) {
      this.publicKeyPromise = getAuthPublicKey()
        .then((res) => {
          this.setData("publicKeyData", {
            isFetching: null,
            fetchStatus: "success",
            publicKey: res.data.publicKey,
          });
        })
        .catch((error) => {
          this.setData("publicKeyData", {
            isFetching: null,
            fetchStatus: "failed",
            publicKey: "",
          });
        })
        .finally(() => {
          this.publicKeyPromise = null;
        });
    }

    if (this.publicKeyPromise) {
      await this.publicKeyPromise;
    }
  }

  setAuthsStoreToIntialState() {
    this.isLoadingFullScreen = false;
    this.isLoadingForLogin = false;
    this.isfirstTimeFamilySelected = false;
    this.isLoadingForFeedback = false;
    this.isLoadingForUploadScreen = false;
    this.isLoadingForSettingsLO = false;
    this.selectFamiliesPageAllImages = {};
    this.loginRandomImage = getImageSrc(Math.floor(Math.random() * 5));
    this.blockedUserData = null;
    this.healthCheckData = "0";
    this.isSessionExpired = false;
    this.isSessionExpiredFromAPI = false;
    this.resolveFn = null;
    this.aesKey = null;
    this.publicKeyData = {
      isFetching: null,
      fetchStatus: "default",
      publicKey: null,
    };
    this.publicKeyPromise = null;
  }

  _employeeLogin({ UserName, Password }, { onFail, onSuccess }) {
    employeeLogin({
      UserName,
      Password,
    })
      .then(({ data }) => {
        onSuccess(data);
      })
      .catch((err) => {
        onFail(err);
      });
  }
}

const AuthS = new AuthStore();

export default AuthS;
